import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://greenlyelectronicsrecycling.com';

const sections = [
  {
    id: 'information-we-collect',
    title: '1. Information We Collect',
    content: [
      {
        subtitle: 'Information You Provide Directly',
        text: 'When you contact us, request a pickup, or submit a drop-off form, we may collect your name, email address, phone number, service address, and any details you include in your message. This information is used solely to respond to your inquiry and fulfill your service request.',
      },
      {
        subtitle: 'Automatically Collected Information',
        text: 'When you visit our website, we may automatically collect certain technical information such as your IP address, browser type, operating system, referring URLs, and pages visited. This data helps us understand how visitors use our site and improve our services.',
      },
    ],
  },
  {
    id: 'how-we-use',
    title: '2. How We Use Your Information',
    content: [
      {
        subtitle: '',
        text: 'We use the information we collect to: respond to your service requests and inquiries; schedule and coordinate electronics recycling pickups and drop-offs; send service confirmations and follow-up communications; improve our website and customer experience; comply with applicable laws and regulations, including CalRecycle reporting requirements.',
      },
    ],
  },
  {
    id: 'data-destruction',
    title: '3. Data Destruction & Device Privacy',
    content: [
      {
        subtitle: 'Hard Drive & Device Destruction',
        text: 'Greenly Electronics Recycling takes data security seriously. All hard drives and storage devices accepted for recycling are physically destroyed or securely wiped in accordance with NIST 800-88 standards. We provide Certificates of Destruction upon request for business clients.',
      },
      {
        subtitle: 'No Data Recovery',
        text: 'We do not attempt to recover, access, or use any data stored on devices submitted for recycling. All devices are processed for material recovery only.',
      },
    ],
  },
  {
    id: 'sharing',
    title: '4. Sharing of Information',
    content: [
      {
        subtitle: '',
        text: 'We do not sell, rent, or trade your personal information to third parties. We may share information with trusted service providers who assist us in operating our website or conducting our business, provided those parties agree to keep this information confidential. We may also disclose information when required by law or to protect the rights and safety of our company and customers.',
      },
    ],
  },
  {
    id: 'cookies',
    title: '5. Cookies & Tracking',
    content: [
      {
        subtitle: '',
        text: 'Our website may use cookies and similar tracking technologies to enhance your browsing experience and analyze site traffic. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, some features of our website may not function properly without cookies.',
      },
    ],
  },
  {
    id: 'data-security',
    title: '6. Data Security',
    content: [
      {
        subtitle: '',
        text: 'We implement reasonable technical and organizational measures to protect your personal information from unauthorized access, disclosure, alteration, or destruction. However, no method of transmission over the internet or electronic storage is 100% secure, and we cannot guarantee absolute security.',
      },
    ],
  },
  {
    id: 'retention',
    title: '7. Data Retention',
    content: [
      {
        subtitle: '',
        text: 'We retain personal information only as long as necessary to fulfill the purposes for which it was collected, including satisfying legal, accounting, or reporting requirements. Contact form submissions and service records are typically retained for up to 3 years.',
      },
    ],
  },
  {
    id: 'your-rights',
    title: '8. Your Rights (California Residents)',
    content: [
      {
        subtitle: 'CCPA Rights',
        text: 'Under the California Consumer Privacy Act (CCPA), California residents have the right to: know what personal information we collect and how it is used; request deletion of personal information we hold about you; opt out of the sale of personal information (we do not sell personal data); and not be discriminated against for exercising these rights.',
      },
      {
        subtitle: 'How to Exercise Your Rights',
        text: 'To submit a data request, please contact us at info@greenly.us or call (213) 600-1000. We will respond to verified requests within 45 days.',
      },
    ],
  },
  {
    id: 'third-party',
    title: '9. Third-Party Links',
    content: [
      {
        subtitle: '',
        text: 'Our website may contain links to third-party websites. We are not responsible for the privacy practices or content of those sites. We encourage you to review the privacy policies of any third-party sites you visit.',
      },
    ],
  },
  {
    id: 'changes',
    title: '10. Changes to This Policy',
    content: [
      {
        subtitle: '',
        text: 'We may update this Privacy Policy from time to time. When we do, we will revise the "Last Updated" date at the top of this page. We encourage you to review this policy periodically to stay informed about how we protect your information.',
      },
    ],
  },
  {
    id: 'contact',
    title: '11. Contact Us',
    content: [
      {
        subtitle: '',
        text: 'If you have any questions or concerns about this Privacy Policy or our data practices, please contact us at: Greenly Electronics Recycling, 2542 W. 3rd Street, Los Angeles, CA 90057. Email: info@greenly.us. Phone: (213) 600-1000.',
      },
    ],
  },
];

const lastUpdated = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

const PrivacyPolicyPage = () => (
  <>
    <title>Privacy Policy | Greenly Electronics Recycling Los Angeles</title>
    <meta name="description" content="Greenly Electronics Recycling Privacy Policy — how we collect, use & protect your personal information. CCPA compliant. Data destruction practices explained. Los Angeles, CA." />
    <meta name="robots" content="index, follow" />
    <meta name="last-modified" content="2026-05-11" />
    <link rel="canonical" href={`${SITE_URL}/privacy-policy`} />
    <script type="application/ld+json">{JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": `${SITE_URL}/privacy-policy#webpage`,
          "url": `${SITE_URL}/privacy-policy`,
          "name": "Privacy Policy | Greenly Electronics Recycling Los Angeles",
          "description": "Read Greenly Electronics Recycling's Privacy Policy — how we collect, use, and protect your personal information. CCPA compliant. Los Angeles, CA.",
          "isPartOf": { "@id": `${SITE_URL}/#website` },
          "about": { "@id": `${SITE_URL}/#business` },
          "breadcrumb": { "@id": `${SITE_URL}/privacy-policy#breadcrumb` }
        },
        {
          "@type": "BreadcrumbList",
          "@id": `${SITE_URL}/privacy-policy#breadcrumb`,
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": `${SITE_URL}/` },
            { "@type": "ListItem", "position": 2, "name": "Privacy Policy", "item": `${SITE_URL}/privacy-policy` }
          ]
        }
      ]
    })}</script>
    <Navbar />
    <main>
      {/* Hero */}
      <section className="pt-16" style={{ background: 'linear-gradient(135deg, #f0faf4 0%, #e8f5ee 40%, #f5fbf7 100%)' }}>
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16">
          <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
            <Link to="/" className="hover:text-[#1B6B3A] transition-colors cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line text-gray-400"></i>
            <span className="text-gray-700 font-medium">Privacy Policy</span>
          </div>
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-[#1B6B3A] rounded-xl shrink-0 mt-1">
              <i className="ri-shield-check-line text-white text-xl"></i>
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 mb-2">
                Privacy <span className="text-[#1B6B3A]">Policy</span>
              </h1>
              <p className="text-gray-500 text-sm">Last Updated: {lastUpdated} &nbsp;·&nbsp; Effective: January 1, 2024</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="bg-white py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex flex-col lg:flex-row gap-12">

            {/* Sidebar TOC */}
            <aside className="lg:w-64 shrink-0">
              <div className="sticky top-24">
                <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">On This Page</p>
                <nav className="space-y-1.5">
                  {sections.map((s) => (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      className="block text-sm text-gray-500 hover:text-[#1B6B3A] transition-colors cursor-pointer leading-snug py-0.5"
                    >
                      {s.title}
                    </a>
                  ))}
                </nav>
                <div className="mt-8 p-4 bg-[#E8F5EE] rounded-xl">
                  <p className="text-xs text-[#1B6B3A] font-semibold mb-2">Questions?</p>
                  <p className="text-xs text-gray-600 mb-3 leading-relaxed">Contact us about your data rights or privacy concerns.</p>
                  <a
                    href="mailto:info@greenly.us"
                    className="inline-flex items-center gap-1.5 text-xs text-[#1B6B3A] font-bold hover:underline cursor-pointer"
                  >
                    <i className="ri-mail-line"></i>
                    info@greenly.us
                  </a>
                </div>
                <div className="mt-4 p-4 bg-gray-50 rounded-xl">
                  <p className="text-xs text-gray-500 font-semibold mb-2">Related Policies</p>
                  <Link to="/terms-of-service" className="flex items-center gap-1.5 text-xs text-[#1B6B3A] hover:underline cursor-pointer">
                    <i className="ri-file-text-line"></i>
                    Terms of Service
                  </Link>
                </div>
              </div>
            </aside>

            {/* Main content */}
            <div className="flex-1 min-w-0">
              <div className="bg-[#E8F5EE] border border-[#c8e6d4] rounded-xl px-5 py-4 mb-10 flex gap-3 items-start">
                <div className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
                  <i className="ri-information-line text-[#1B6B3A]"></i>
                </div>
                <p className="text-sm text-[#1B6B3A] leading-relaxed">
                  Greenly Electronics Recycling (&quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) is committed to protecting your privacy. This policy explains how we collect, use, and safeguard your information when you visit our website or use our services.
                </p>
              </div>

              <div className="space-y-10">
                {sections.map((section) => (
                  <div key={section.id} id={section.id} className="scroll-mt-28">
                    <h2 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">{section.title}</h2>
                    <div className="space-y-4">
                      {section.content.map((block, idx) => (
                        <div key={idx}>
                          {block.subtitle && (
                            <h3 className="text-sm font-semibold text-gray-800 mb-1.5">{block.subtitle}</h3>
                          )}
                          <p className="text-gray-600 text-sm leading-relaxed">{block.text}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>

              {/* Bottom CTA */}
              <div className="mt-14 pt-8 border-t border-gray-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-semibold text-gray-800 mb-1">Have a data request or privacy concern?</p>
                  <p className="text-xs text-gray-500">We respond to all verified requests within 45 days.</p>
                </div>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-[#1B6B3A] hover:bg-[#2D8653] text-white text-sm font-semibold px-5 py-2.5 rounded-md transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-mail-send-line"></i>
                  Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
    <Footer />
  </>
);

export default PrivacyPolicyPage;
